/**
 * The country-gating vocabulary and paths, shared by the read and the writes.
 *
 * # Why this is its own module
 *
 * `jurisdictions.ts` reads the gate; `jurisdictioncontrols.ts` moves it. Both
 * need the same closed set of capabilities and the same two paths, and the read
 * module imports the controls module to assemble the screen — so putting the
 * shared half in either one makes them import each other. The same argument
 * `index.ts` records about `LIVE_SCREENS` and `provenance.ts`: the contract goes
 * one level down, and both sides import it rather than one importing the other.
 *
 * It is also the structural version of a claim the two modules would otherwise
 * only make in prose. A capability vocabulary that exists in one place cannot
 * drift between the screen that renders a mandate and the control that writes
 * one, and drift here is not cosmetic — a capability the write side can grant
 * and the read side calls unknown is a mandate an operator is told the console
 * cannot understand.
 */

/**
 * The closed set, in the order `internal/jurisdiction` declares it.
 *
 * Phase-2 entries are vocabulary, not reachable features: a mandate row can be
 * written ahead of the build, and both sides have to handle one without calling
 * it unknown.
 *
 * # `internal_transfer` was missing for months, and the failure was silent
 *
 * This console transcribed the set at go-wallet#37. `internal_transfer` was
 * added to it at go-wallet#146 — FR-17, sending to another customer of the
 * programme, kept as its own capability precisely so that reading `cash_out` as
 * permission for it could not happen. Nothing told this repository, and there
 * was nowhere for the news to land.
 *
 * The consequence was not a crash. A country whose mandate covers
 * `internal_transfer` parsed, rendered the capability verbatim, and then counted
 * itself as "a capability this console does not know" — which downgrades the
 * whole screen from `Live` to `Partial` and files a gap in the "Not shown" note.
 * A compliance officer reading a correct mandate would have been told the
 * console could not read it.
 *
 * That is the failure this module exists to make structurally impossible rather
 * than to warn about.
 */
export const KNOWN_CAPABILITIES: readonly string[] = [
  'receive', 'cash_out', 'storefront', 'statements',
  'internal_transfer',
  'card', 'inbound_push', 'multi_currency', 'crypto_ramp',
]

/** The same set as a lookup, for the read side's per-entry check. */
export const CAPABILITY_SET: ReadonlySet<string> = new Set<string>(KNOWN_CAPABILITIES)

/**
 * The collection route: `GET` lists the gates, `POST` declares a country.
 *
 * One constant for both, because they are the same resource and a second string
 * is a second thing to get wrong when the surface moves.
 */
export const JURISDICTIONS_PATH = '/jurisdictions'

/**
 * The per-country route. `country` is an alpha-2 code; the backend 400s
 * otherwise, and `isAlpha2` on both sides refuses the shape before it is sent.
 *
 * The write routes hang off this one — `/open`, `/close`, `/mandate-scope` and
 * `/mandate-scope/narrow` are all suffixes of it, and building them from here
 * means a country code is escaped and normalised once.
 */
export function jurisdictionPath(country: string): string {
  return `/jurisdictions/${country}`
}
